import { z } from 'zod';
import { IsoDateTime, Uuid, withDefaults } from './common.js';
import { PreRegistrationCreate, PreRegistration } from './preregistrations.js';
/** Module `companion` (docs/modules/companion.md, ADR 0015). Owner: task S15; the app (M1-M3) uses these shapes. */
export const CompanionDeviceInfo = z.object({
    name: z.string().trim().min(1).max(80),
    platform: z.enum(['android', 'ios']),
    model: z.string().max(80).nullable().default(null),
    osVersion: z.string().max(40).nullable().default(null),
    appVersion: z.string().max(40),
    bundleVersion: z.string().max(40).nullable().default(null),
});
// ---------- Auth (public) ----------
/** POST /companion/auth/request-link (always 202). */
export const CompanionLinkRequest = z.object({ email: z.email().max(254) });
/** POST /companion/auth/exchange */
export const CompanionExchange = z.object({ token: z.string().min(20).max(2000), device: CompanionDeviceInfo });
export const CompanionExchangeResult = z.object({
    /** Shown once; the app keeps it in secure storage and sends `Authorization: Companion <token>`. */
    token: z.string(),
    deviceId: Uuid,
    tenantName: z.string(),
    memberName: z.string(),
});
// ---------- Me ----------
/** What the person may do in the app (from their group's Companion settings). */
export const CompanionPermissions = z.object({
    mobileSignIn: z.boolean(),
    locationSignIn: z.boolean(),
    preRegister: z.boolean(),
    ownHistory: z.boolean(),
    todayView: z.boolean(),
    /** Phase 3 (evacuation module). */
    evacuationList: z.boolean(),
});
export const CompanionSite = z.object({
    id: Uuid,
    name: z.string(),
    timeZone: z.string(),
    isRemote: z.boolean(),
    /** Only when location sign-in is allowed for the person and the site has a geofence. */
    geofence: z.object({ lat: z.number(), lng: z.number(), radiusM: z.number().int() }).nullable(),
});
export const CompanionOpenVisit = z.object({ visitId: Uuid, siteId: Uuid, siteName: z.string(), signedInAt: IsoDateTime });
export const CompanionMe = z.object({
    memberId: Uuid,
    fullName: z.string(),
    groupName: z.string(),
    tenantName: z.string(),
    photoThumbUrl: z.string().nullable(),
    /** CS1.M code to show at the kiosk. */
    qrPayload: z.string(),
    permissions: CompanionPermissions,
    requireAppLock: z.boolean(),
    sites: z.array(CompanionSite),
    openVisit: CompanionOpenVisit.nullable(),
    /** Sign-out-first groups: the person counts as in unless signed out today. */
    signOutFirst: z.boolean(),
    push: z.enum(['enabled', 'disabled', 'unavailable']),
    locationConsent: z.object({ given: z.boolean(), version: z.string().nullable(), currentVersion: z.string() }),
    /** The group this person may pre-register visitors into; null when they may not (older servers omit it). */
    visitorGroupId: Uuid.nullable().default(null),
    /** The person's group stores sign-in locations, so the app should send coordinates (older servers omit it). */
    storesLocation: z.boolean().default(false),
});
export const CompanionSignIn = z.object({
    idempotencyKey: Uuid,
    siteId: Uuid,
    occurredAt: IsoDateTime,
    method: z.enum(['manual', 'geofence', 'qr']).default('manual'),
    /** Sent only when the group stores locations; rounded to 4 decimals by the app. */
    location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), accuracyM: z.number().min(0).max(10000) }).nullable().default(null),
});
export const CompanionSignOut = CompanionSignIn.omit({ siteId: true }).extend({ siteId: Uuid.nullable().default(null), visitId: Uuid.nullable().default(null) });
export const CompanionSignResult = z.object({ status: z.enum(['applied', 'merged', 'pending', 'rejected']), visitId: Uuid.nullable(), messageKey: z.string().nullable() });
export const CompanionHistoryItem = z.object({
    visitId: Uuid,
    siteName: z.string(),
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime.nullable(),
    signInMethod: z.string(),
    signOutMethod: z.string().nullable(),
    timeZone: z.string(),
});
export const CompanionHistoryQuery = z.object({ before: IsoDateTime.optional(), limit: z.coerce.number().int().min(1).max(100).default(30) });
export const CompanionHistory = z.object({ items: z.array(CompanionHistoryItem), nextBefore: IsoDateTime.nullable() });
/** Today view (if permitted): who is in at a site, names only (plus photo thumbs when the site shows them). */
export const CompanionTodayQuery = z.object({ siteId: Uuid });
export const CompanionToday = z.object({
    siteId: Uuid,
    people: z.array(z.object({ name: z.string(), groupName: z.string(), signedInAt: IsoDateTime, isVisitor: z.boolean(), hostName: z.string().nullable() })),
});
/** Visitors the person is hosting today (with pre-arrival progress). */
export const CompanionMyVisitors = z.object({ items: z.array(PreRegistration) });
export const CompanionPreRegister = PreRegistrationCreate.omit({ hostMemberId: true });
/** Quick reply to "your visitor has arrived" (push action or in-app). */
export const HostReply = z.enum(['on_my_way', 'please_wait']);
export const CompanionVisitReply = z.object({ reply: HostReply });
export const CompanionPushRegister = z.object({
    /** ExponentPushToken[...] */
    expoPushToken: z.string().max(200).nullable(),
    enabled: z.boolean(),
});
export const CompanionLocationConsent = z.object({ given: z.boolean(), version: z.string().max(20) });
export const CompanionDeviceUpdate = z.object({ appVersion: z.string().max(40), bundleVersion: z.string().max(40).nullable(), appLockSet: z.boolean() });
// ---------- Portal ----------
export const CompanionDevice = z.object({
    id: Uuid,
    memberId: Uuid,
    name: z.string(),
    platform: z.string(),
    appVersion: z.string().nullable(),
    push: z.boolean(),
    locationConsent: z.boolean(),
    appLockSet: z.boolean(),
    lastSeenAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    revokedAt: IsoDateTime.nullable(),
});
export const CompanionInviteRequest = z.object({
    /** One member, or every current member of the group with an email (bulk). */
    memberIds: z.array(Uuid).min(1).max(5000).optional(),
    groupId: Uuid.optional(),
    send: z.enum(['email', 'none']).default('email'),
});
export const CompanionInviteResult = z.object({
    sent: z.number().int(),
    skippedNoEmail: z.number().int(),
    /** Only for a single member with send = 'none': the link to show as a QR on screen. */
    link: z.string().nullable(),
});
// ---------- Settings ----------
export const CompanionGroupSettings = z.object({
    enabled: z.boolean().default(false),
    permissions: withDefaults(z.object({
        mobileSignIn: z.boolean().default(true),
        locationSignIn: z.boolean().default(false),
        preRegister: z.boolean().default(false),
        ownHistory: z.boolean().default(true),
        todayView: z.boolean().default(false),
        evacuationList: z.boolean().default(false),
    })),
    requireAppLock: z.boolean().default(false),
    /** Members can ask for their own invite at the kiosk (spec 2.4 Details). */
    selfInviteFromKiosk: z.boolean().default(false),
    /** Group that members' pre-registered visitors go into. Empty = the first active standard group. */
    visitorGroupId: Uuid.nullable().default(null),
});
/** Kiosk config section `companion`: groups whose members may ask for their own invite at the kiosk (Companion on and "send themselves an invite" on). */
export const CompanionKioskSection = z.object({ selfInviteGroupIds: z.array(Uuid).default([]) });
export const CompanionSiteSettings = z.object({
    geofence: withDefaults(z.object({
        enabled: z.boolean().default(false),
        lat: z.number().min(-90).max(90).default(0),
        lng: z.number().min(-180).max(180).default(0),
        radiusM: z.number().int().min(50).max(2000).default(150),
    })),
});
export const CompanionTenantSettings = z.object({
    inviteDays: z.number().int().min(1).max(30).default(7),
    /** Text version of the in-app location notice; bump to ask everyone again. */
    locationNoticeVersion: z.string().max(20).default('1'),
});
//# sourceMappingURL=companion.js.map